import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '../Tag';
import { SectionHead } from '../SectionHead';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { Photo } from './Photo';
import { PaintThickness } from './PaintThickness';
import { CheckRow } from './CheckRow';
import {
    CameraOutlined,
    CarOutlined,
    DownOutlined,
    FileDoneOutlined,
    NodeIndexOutlined,
    UpOutlined,
    VideoCameraOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { SectionIcon } from '../SectionIcon';

const ALL_PARTS = ['photos', 'paint', 'checks', 'damages', 'documents', 'video'];
// damages shown before "Show more" (one row of the grid); the rest are always printed
export const DAMAGE_LIMIT = 4;

// S1: our own inspection of the car. `parts` picks the blocks to show (the two-column layout splits them up)
export const Inspection = ({ inspection, onOpenImage, parts = ALL_PARTS }) => {
    const { t } = useTranslation('reportPage');
    const has = part => parts.includes(part);
    const [allDamages, setAllDamages] = useState(false);
    const moreDamages = Math.max(0, inspection.damages.length - DAMAGE_LIMIT);

    return (
        <>
            {has('photos') && inspection.photos.length > 0 && (
                <section id="photos" className="card" aria-labelledby="photos-title" style={{ gap: 16 }}>
                    <SectionHead
                        id="photos-title"
                        icon={CameraOutlined}
                        title={t('inspection.photosTitle')}
                        sub={inspection.photoSub}
                    />
                    <div className="gallery">
                        {inspection.photos.map((photo, i) => (
                            <Photo photo={photo} onOpen={onOpenImage} key={photo.url || `${photo.label}-${i}`} />
                        ))}
                    </div>
                </section>
            )}

            {has('paint') && inspection.paint.panels.length > 0 && <PaintThickness paint={inspection.paint} />}

            {has('checks') && inspection.checkGroups.length > 0 && (
                <section id="checks" className="card" aria-labelledby="checks-title" style={{ gap: 16 }}>
                    <SectionHead
                        id="checks-title"
                        icon={CarOutlined}
                        title={t('inspection.checksTitle')}
                        sub={inspection.checkSub}
                    />
                    <div className="chk-grid">
                        {inspection.checkGroups.map((group, groupIndex) => (
                            <div className="hgroup" key={groupIndex}>
                                <h3 className="eyebrow">{group.name}</h3>
                                {group.items.map((item, itemIndex) => (
                                    <CheckRow item={item} key={itemIndex} />
                                ))}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {has('damages') && inspection.damages.length > 0 && (
                <section id="damages" className="card" aria-labelledby="damages-title" style={{ gap: 16 }}>
                    <SectionHead
                        id="damages-title"
                        icon={WarningOutlined}
                        title={t('inspection.damagesTitle')}
                        sub={inspection.damageSub}
                    />
                    <div className="dmg-grid">
                        {inspection.damages.map((damage, i) => (
                            <article
                                className={`dmg${i >= DAMAGE_LIMIT && !allDamages ? ' more' : ''}`}
                                key={`${damage.area}-${i}`}
                            >
                                {damage.photo ? (
                                    <Photo photo={{ url: damage.photo, label: damage.area }} onOpen={onOpenImage} />
                                ) : (
                                    <PhotoPlaceholder label={damage.area} />
                                )}
                                <div
                                    style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 10px' }}
                                >
                                    <span style={{ fontSize: 15, fontWeight: 600 }}>{damage.area}</span>
                                    {damage.severity && <Tag tone={damage.style}>{damage.severity}</Tag>}
                                </div>
                                {damage.text && (
                                    <span style={{ fontSize: 14, color: 'var(--tokens-text-secondary)' }}>
                                        {damage.text}
                                    </span>
                                )}
                            </article>
                        ))}
                    </div>
                    {moreDamages > 0 && (
                        <button
                            type="button"
                            className="textlink chips-more noprint"
                            onClick={() => setAllDamages(current => !current)}
                        >
                            {allDamages ? <UpOutlined /> : <DownOutlined />}{' '}
                            {allDamages ? t('layout.showLess') : t('layout.showMore', { count: moreDamages })}
                        </button>
                    )}
                </section>
            )}

            {has('documents') && (inspection.documents.length > 0 || inspection.drive.length > 0) && (
                <div className="two">
                    {inspection.documents.length > 0 && (
                        <section id="documents" className="card" aria-labelledby="documents-title" style={{ gap: 16 }}>
                            <h2 id="documents-title" className="h2">
                                <SectionIcon icon={FileDoneOutlined} />
                                {t('inspection.documentsTitle')}
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {inspection.documents.map((doc, docIndex) => (
                                    <CheckRow item={doc} key={docIndex} />
                                ))}
                            </div>
                        </section>
                    )}
                    {inspection.drive.length > 0 && (
                        <section id="drive" className="card" aria-labelledby="drive-title" style={{ gap: 16 }}>
                            <h2 id="drive-title" className="h2">
                                <SectionIcon icon={NodeIndexOutlined} />
                                {t('inspection.driveTitle')}
                            </h2>
                            <div className="ai-text" style={{ fontSize: 15 }}>
                                {inspection.drive.map((paragraph, lineIndex) => (
                                    <p key={lineIndex}>{paragraph}</p>
                                ))}
                            </div>
                            {inspection.driveSub && <span className="muted">{inspection.driveSub}</span>}
                        </section>
                    )}
                </div>
            )}

            {has('video') && inspection.videoUrl && (
                <section id="video" className="card" aria-labelledby="video-title" style={{ gap: 16 }}>
                    <SectionHead
                        id="video-title"
                        icon={VideoCameraOutlined}
                        title={t('inspection.videoTitle')}
                        sub={inspection.video}
                    />
                    <div className="video noprint">
                        <video
                            src={inspection.videoUrl}
                            controls
                            preload="metadata"
                            aria-label={t('inspection.videoTitle')}
                        />
                    </div>
                </section>
            )}
        </>
    );
};
